import React from "react";
import { getStudentLife } from "../api/studentLifeApi";
import StudentLifeCard from "../components/StudentLifeCard";

const StudentLife = () => {
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [modalProject, setModalProject] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 9;

  const handleSeeMoreProject = (project) => setModalProject(project);
  const closeModalProject = () => setModalProject(null);

  React.useEffect(() => {
    let isMounted = true;
    const fetchStudentLife = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getStudentLife();
        if (!response.success) return;
        if (isMounted) setProjects(response.data);
      } catch {
        if (isMounted)
          setError(
            "Failed to load student life projects. Please try again later."
          );
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchStudentLife();
    return () => {
      isMounted = false;
    };
  }, []);

  // Prevent background scroll when modal is open
  React.useEffect(() => {
    if (modalProject) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [modalProject]);

  // pagination logic
  const totalPages = Math.ceil(projects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = projects.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* Full-page animated/gradient background */}
      <div
        className="fixed inset-0 -z-10 bg-gradient-to-br from-blue-200 via-white to-blue-400 dark:from-gray-900 dark:via-gray-950 dark:to-blue-900 animate-fadein"
        aria-hidden="true"
      />

      {/* Main content */}
      <div className="min-h-screen bg-transparent transition-all duration-500">
        {/* Header Section */}
        <section className="pt-24 pb-12 mb-10 relative">
          <div className="absolute inset-0 pointer-events-none select-none">
            <div className="w-full h-full bg-gradient-to-tr from-blue-200/60 via-blue-100/30 to-transparent dark:from-blue-900/40 dark:via-gray-900/30 dark:to-transparent blur-2xl animate-fadein" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-black tracking-tight drop-shadow-lg animate-fadein font-manrope">
                Student Life
              </h1>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 relative z-10">
          {loading && (
            <div className="text-center text-blue-500 py-8 animate-pulse">
              Loading projects...
            </div>
          )}
          {error && !loading && (
            <div className="text-center text-red-600 py-8 animate-fadein">
              {error}
            </div>
          )}
          {!loading && !error && projects.length === 0 && (
            <div className="text-center text-gray-500 py-8 animate-fadein">
              No projects found.
            </div>
          )}

          {/* Projects Grid */}
          {!loading && !error && projects.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16 animate-fadein">
                {currentItems.map((project, id) => (
                  <StudentLifeCard
                    key={id}
                    item={project}
                    onClick={handleSeeMoreProject}
                  />
                ))}
              </div>

              {/* Pagination Controls */}
              <div className="flex justify-center items-center gap-2 mb-16 animate-fadein select-none">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  className="flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-blue-300 text-blue-700 font-bold shadow-md hover:from-blue-200 hover:to-blue-400 transition disabled:opacity-50 disabled:cursor-not-allowed border border-blue-200"
                  aria-label="Previous page"
                >
                  <svg
                    className="w-5 h-5 mr-1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Prev
                </button>

                <div className="flex items-center gap-1 px-4 py-2 rounded-full bg-white/80 dark:bg-gray-800/80 shadow border border-blue-100 dark:border-gray-700">
                  <span className="text-lg font-bold text-blue-700 dark:text-blue-300 tracking-wider">
                    {currentPage}
                  </span>
                  <span className="mx-1 text-gray-500 dark:text-gray-400 font-semibold">
                    /
                  </span>
                  <span className="text-lg font-bold text-blue-500 dark:text-blue-200 tracking-wider">
                    {totalPages}
                  </span>
                </div>

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  className="flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-blue-300 text-blue-700 font-bold shadow-md hover:from-blue-200 hover:to-blue-400 transition disabled:opacity-50 disabled:cursor-not-allowed border border-blue-200"
                  aria-label="Next page"
                >
                  Next
                  <svg
                    className="w-5 h-5 ml-1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </>
          )}

          {/* Modal Popup for Project Details */}
          {modalProject && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xl animate-fadein">
              <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-lg w-full p-0 relative border-4 border-blue-600/80 glassmorphism animate-fadein">
                <button
                  onClick={closeModalProject}
                  aria-label="Close"
                  className="absolute top-3 right-3 text-gray-400 hover:text-blue-600 text-3xl font-bold bg-white/80 dark:bg-gray-800/80 rounded-full w-10 h-10 flex items-center justify-center shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  ✕
                </button>
                <div className="rounded-t-3xl overflow-hidden">
                  <img
                    src={modalProject["images/videos"] || "/about/mech.jpg"}
                    alt={modalProject.title}
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/about/mech.jpg";
                    }}
                    className="w-full h-64 object-cover shadow-lg"
                  />
                </div>
                <div className="p-10">
                  <h3 className="text-3xl font-extrabold text-blue-700 dark:text-blue-300 mb-4 text-center drop-shadow animate-fadein">
                    {modalProject.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-200 text-lg text-center leading-relaxed animate-fadein">
                    {modalProject.description}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentLife;
